/**
 * What a client says they need, and what that means contractually.
 *
 * Two different questions, deliberately kept apart:
 *
 *   serviceLine     what the client asked for, in Altitude's own words. Five
 *                   choices, stored verbatim, shown back to staff.
 *   engagementType  which agreement that implies - management, consulting or
 *                   both. Three values, and the thing the portal acts on: it
 *                   decides the milestone plan seeded at approval and which
 *                   documents go in the standard pack.
 *
 * Collapsing them would force a choice between a registration form that says
 * what Altitude actually sells and a contract model with one plan per service
 * line. This way the form speaks the client's language and the machinery
 * behind it stays at three cases, which is as many as there are agreements.
 *
 * The mapping is a table rather than a function body so it can be read, and
 * tested, as data. When a service line should get its own phase plan, that is
 * a change here plus a plan in milestonePlans.ts - not a rewrite.
 */

export type EngagementType = "management" | "consulting" | "both";

export interface ServiceLine {
  value: string;
  label: string;
  /** Shown under the dropdown so the choice is not a guess. */
  hint: string;
  engagementType: EngagementType;
}

export const SERVICE_LINES: readonly ServiceLine[] = [
  {
    value: "construction_management",
    label: "Facility Construction Management",
    hint: "Running the build: contractors, schedule, budget and handover.",
    // A project engagement, not operating the club afterwards.
    engagementType: "consulting",
  },
  {
    value: "design_build_out",
    label: "Facility Design & Build-Out",
    hint: "Layout, equipment specification and fit-out of a new or renovated space.",
    engagementType: "consulting",
  },
  {
    value: "operations_consulting",
    label: "Facility Operations Consulting",
    hint: "Improving how an open facility runs - staffing, programming, retention, margin.",
    engagementType: "consulting",
  },
  {
    value: "gym_management",
    label: "Gym Management",
    hint: "Altitude runs the facility day to day under a management agreement.",
    engagementType: "management",
  },
  {
    value: "general",
    label: "General Management & Consulting",
    hint: "A combined engagement, or not sure yet - we will scope it with you.",
    engagementType: "both",
  },
];

export const SERVICE_LINE_VALUES = SERVICE_LINES.map(line => line.value) as [string, ...string[]];

export const serviceLine = (value: string | null | undefined): ServiceLine | undefined =>
  SERVICE_LINES.find(line => line.value === value);

/**
 * The contract type implied by a service line.
 *
 * Falls back to "both" for anything unrecognised. That is the conservative
 * direction: "both" seeds the union of the two milestone plans and issues both
 * agreements, so a stale value produces too much to review rather than an
 * engagement silently missing half its plan.
 */
export function engagementTypeFor(value: string | null | undefined): EngagementType {
  return serviceLine(value)?.engagementType ?? "both";
}

/** The label to show staff, falling back to the raw value rather than a blank. */
export const serviceLineLabel = (value: string | null | undefined): string =>
  serviceLine(value)?.label ?? (value || "Not specified");
